import { type NotificaResponseDTO } from '@framework/api-client';
import {
  ellipsisExpandTextSymbol,
  FlexRow,
  ISORelativeDate,
} from '@framework/ui';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import { darken } from 'polished';
import styled from 'styled-components';
import { ButtonNotificationRead } from './ButtonNotificationRead';
import { ListItemMetaNotification } from './ListItemMetaNotification';
import { ListItemNotification } from './ListItemNotification';
import { NotificationIconType } from './NotificationIconType';
import { NotificationPriorityTag } from './NotificationPriorityTag';
import { ReadIndicator } from './ReadIndicator';

const SecondaryText = styled.span`
  color: ${props => props.theme.colorTextSecondary};
  font-size: 12px;
  display: inline-flex;
  line-height: 1.3;
  padding-bottom: 6px;
`;
const TitleParagraphH3Wrapper = styled(Typography.Title)`
  &.ant-typography {
    margin: 0;
    font-size: ${props => props.theme.fontSize}px;
    font-weight: 600;
  }
  .ant-typography-expand {
    color: ${props => darken(0.1, props.theme.colorPrimary)};
    font-weight: 400;
  }
`;
const TitleParagraph = styled(Typography.Paragraph)`
  &&& {
    margin-bottom: 0;
    .ant-typography-expand {
      color: ${props => darken(0.1, props.theme.colorPrimary)};
      font-weight: 400;
    }
  }
`;

const TextRow = styled.div<{ read?: boolean }>`
  ${props => props.read && 'opacity: 0.75;'}
`;
const DescriptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

interface NotificationListItemProps {
  notification: NotificaResponseDTO;
}

/**
 * Singola notifica
 */
export function NotificationListItem(
  props: Readonly<NotificationListItemProps>,
) {
  const { notification } = props;

  return (
    <ListItemNotification type={notification.tipo}>
      <FlexRow
        style={{ maxWidth: '100%' }}
        right={
          <ReadIndicator
            key={`read-indicator-${notification.id}`}
            read={
              notification.dataPresaVisione
                ? dayjs(notification.dataPresaVisione).toDate()
                : null
            }
          />
        }
      >
        <ListItemMetaNotification
          title={
            <TextRow read={!!notification.dataPresaVisione}>
              {/* Riga Struttura */}
              {notification.nomeStrutturaMittente && (
                <SecondaryText>
                  {notification.nomeStrutturaMittente}
                </SecondaryText>
              )}
              {/* Riga Oggetto */}
              {notification.oggetto && (
                <TitleParagraphH3Wrapper
                  level={3}
                  ellipsis={{
                    expandable: 'collapsible',
                    rows: 1,
                    symbol: ellipsisExpandTextSymbol,
                  }}
                >
                  <NotificationIconType type={notification.tipo as any} />
                  {notification.oggetto}
                </TitleParagraphH3Wrapper>
              )}
              {/* Riga Messaggio */}
              <TitleParagraph
                ellipsis={{
                  expandable: 'collapsible',
                  rows: 3,
                  symbol: ellipsisExpandTextSymbol,
                }}
              >
                {!notification.oggetto && (
                  // Se non è presente un oggetto, visualiziamo qui l'icona del tipo di notifica
                  <NotificationIconType type={notification.tipo as any} />
                )}
                {notification.messaggio}
              </TitleParagraph>
            </TextRow>
          }
          description={
            <DescriptionRow>
              <div>
                <NotificationPriorityTag {...props} />
                <SecondaryText>
                  <ISORelativeDate
                    date={notification.dataNotifica}
                    relativeOnly
                  />
                </SecondaryText>
              </div>
              <ButtonNotificationRead
                notificationId={notification.id!}
                read={
                  notification.dataPresaVisione
                    ? dayjs(notification.dataPresaVisione).toDate()
                    : null
                }
              />
            </DescriptionRow>
          }
        />
      </FlexRow>
    </ListItemNotification>
  );
}
